import { LOCALIZED_PATHS } from '../constants/url-constants';
import { decodeOpaqueUrlPathSegment } from './encode-opaque-url-path-segment';

export type JobDetailRouteParams = {
  jobPath: string;
  jobId: string;
  companyId: string;
  seoSlug: string;
};

const VALID_JOB_PATHS = new Set<string>([
  LOCALIZED_PATHS.en.job,
  LOCALIZED_PATHS.fi.job,
  LOCALIZED_PATHS.sv.job,
]);

function buildJobPathPattern(): string {
  return `(${[...VALID_JOB_PATHS]
    .map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|')})`;
}

/**
 * Parse delocalized job detail paths:
 * - `/job/{jobId}/{companyId}/{seoSlug?}`
 * - `/job/{jobId}//{seoSlug}` (missing company — Typesense hits without companyId)
 */
export function parseJobDetailRoutePath(pathname: string): JobDetailRouteParams | null {
  const path = pathname !== '/' ? pathname.replace(/\/$/, '') : pathname;
  const jobPathPattern = buildJobPathPattern();

  const missingCompanyMatch = path.match(
    new RegExp(`^/${jobPathPattern}/([^/]+)/+/([^/?].+)$`),
  );
  if (missingCompanyMatch) {
    const [, jobPath, jobId, seoSlug] = missingCompanyMatch;
    if (!VALID_JOB_PATHS.has(jobPath)) return null;
    return {
      jobPath,
      jobId,
      companyId: '',
      seoSlug,
    };
  }

  const standardMatch = path.match(
    new RegExp(`^/${jobPathPattern}/([^/]+)/([^/]+)(?:/(.+))?$`),
  );
  if (!standardMatch) return null;

  const [, jobPath, jobId, companyId, seoSlug] = standardMatch;
  if (!VALID_JOB_PATHS.has(jobPath)) return null;

  return {
    jobPath,
    jobId,
    companyId: decodeOpaqueUrlPathSegment(companyId),
    seoSlug: seoSlug ?? '',
  };
}

export function isValidJobDetailRoutePath(pathname: string): boolean {
  return parseJobDetailRoutePath(pathname) !== null;
}
